import { useState } from 'react';

import { useCreateFormStore } from '@components/form';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { AttributeFieldType, TravellerAttributeTemplateData } from 'api/attributes/requests';

import {
  DetailsSection,
  FieldFormData,
  FieldFormSection,
  FieldFormWithUnsavedChangesPrompt,
  Form,
  FormFooter,
  applyInputStatus,
  locale,
} from './field-form-shared';

type FileFieldFormProps = {
  attribute?: TravellerAttributeTemplateData;
  isTravellerAttribute: boolean;
  onSubmit: (data: FieldFormData) => Promise<boolean>;
  isEditing: boolean;
};

export const FileFieldForm = ({
  attribute,
  isTravellerAttribute,
  onSubmit,
  isEditing,
}: FileFieldFormProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();

  const [isAllowMultiple, setIsAllowMultiple] = useState(attribute?.attributeType === 'file_multi');

  const handleSubmit = async (data: FieldFormData) => {
    const attributeType: AttributeFieldType = isAllowMultiple ? 'file_multi' : 'file';
    const submitData = applyInputStatus({ ...data, attributeType }, isTravellerAttribute);
    delete submitData.multipleFiles;
    return onSubmit(submitData);
  };

  return (
    <FieldFormWithUnsavedChangesPrompt onSubmit={handleSubmit} store={formStore}>
      <Stack space={6}>
        <DetailsSection
          attributeType={isAllowMultiple ? 'file_multi' : 'file'}
          attribute={attribute}
          isTravellerAttribute={isTravellerAttribute}
        />

        <Sidebar.Separator />

        <FieldFormSection label={t('options')}>
          <Stack space={1} orientation="horizontal" align="center" justify="space-between">
            <Text font="regular14" text={t('allowMultipleFiles')} />
            {!isEditing ? (
              <Form.Switch
                // @ts-ignore
                id="multipleFiles"
                label={t('allowMultipleFiles')}
                labelVisible={false}
                onChange={setIsAllowMultiple}
                defaultValue={attribute?.attributeType === 'file_multi'}
              />
            ) : (
              <Text
                font="regular14"
                text={isAllowMultiple ? t('core.yes') : t('core.no')}
                color="textHelper"
              />
            )}
          </Stack>
        </FieldFormSection>
      </Stack>
      <FormFooter isEditing={isEditing} isDirty={isDirty} />
    </FieldFormWithUnsavedChangesPrompt>
  );
};
